import type { ProblemIssue } from '@bloom/contracts';

/** A non-2xx answer (problem+json), a connection failure (status 0) or a response that broke its contract. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly issues: readonly ProblemIssue[];
  readonly correlationId: string | null;

  constructor(input: {
    status: number;
    code: string;
    detail: string;
    issues?: readonly ProblemIssue[];
    correlationId?: string | null;
  }) {
    super(input.detail);
    this.name = 'ApiError';
    this.status = input.status;
    this.code = input.code;
    this.issues = input.issues ?? [];
    this.correlationId = input.correlationId ?? null;
  }

  /** No response at all (offline, DNS, aborted connection). */
  get isNetwork(): boolean {
    return this.status === 0;
  }
}

/** The server answered 2xx, but the body does not match the contract's response schema. */
export class ContractError extends ApiError {
  constructor(status: number, issues: readonly ProblemIssue[], correlationId: string | null) {
    super({
      status,
      code: 'contract.response_mismatch',
      detail: 'Response does not match its contract',
      issues,
      correlationId,
    });
    this.name = 'ContractError';
  }
}

/** Query retry policy: only transient failures (network, 5xx), at most twice. */
export function shouldRetry(failureCount: number, error: unknown): boolean {
  if (!(error instanceof ApiError)) return false;
  return failureCount < 2 && (error.isNetwork || error.status >= 500);
}
